import { AttachMoney } from '@mui/icons-material';
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Slider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHotelSearch } from '../Availability/Search/HotelSearchContext';
import { useHotelMetadata } from '@frontend/features/hotel-content';
import { useEffect, useRef } from 'react';

export const HotelFaresFilters = () => {

  const { t } = useTranslation()
  const { findSupplierByContext } = useHotelMetadata();

  const {
    filtersPropertyFares,
    setFiltersPropertyFares,
    filtersPropertyFaresOptions
  } = useHotelSearch();

  const userHasInteractedWithSlider = useRef(false);
  const previousPriceRange = useRef<[number, number]>([0, 1000]);

  const handleChange = (key: keyof typeof filtersPropertyFares, value: any) => {
    setFiltersPropertyFares((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSliderChange = (newValue: [number, number]) => {
    userHasInteractedWithSlider.current = true;
    handleChange('priceRange', newValue);
  };



  useEffect(() => {
    const currentRange = filtersPropertyFaresOptions.priceRange;
    const hasRangeChanged = 
      currentRange[0] !== previousPriceRange.current[0] || 
      currentRange[1] !== previousPriceRange.current[1];

    if (hasRangeChanged) {
      if (!userHasInteractedWithSlider.current) {
        setFiltersPropertyFares((prev) => ({
          ...prev,
          priceRange: currentRange,
        }));
      } else {
        const currentUserRange = filtersPropertyFares.priceRange || currentRange;
        const adjustedRange: [number, number] = [
          Math.max(currentUserRange[0], currentRange[0]),
          Math.min(currentUserRange[1], currentRange[1])
        ];

        if (adjustedRange[0] !== currentUserRange[0] || adjustedRange[1] !== currentUserRange[1]) {
          setFiltersPropertyFares((prev) => ({
            ...prev,
            priceRange: adjustedRange,
          }));
        }
      }

      previousPriceRange.current = currentRange;
    }
  }, [filtersPropertyFaresOptions.priceRange, filtersPropertyFares.priceRange, setFiltersPropertyFares]);

  const isSliderEnabled = filtersPropertyFaresOptions.priceRange[1] > filtersPropertyFaresOptions.priceRange[0];

  // Generate provider options with supplier names
  const providerOptions = filtersPropertyFaresOptions.accessCodeOptions.map(accessCode => {
    const supplier = findSupplierByContext(accessCode);
    return {
      value: accessCode,
      label: supplier?.supplierName || accessCode, // Fallback to accessCode if supplier not found
    };
  });

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">

        <TextField
          placeholder={t('hotels.fares.filters.searchByName')}
          size="small"
          value={filtersPropertyFares.roomName ?? ''}
          onChange={(e) => handleChange('roomName', e.target.value)}
        />

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="board-label">{t('hotels.fares.filters.board')}</InputLabel>
          <Select
            labelId="board-label"
            value={filtersPropertyFares.board || 'all'}
            label="Board"
            size="small"
            onChange={(e) => handleChange('board', e.target.value === 'all' ? '' : e.target.value)}
          >
            <MenuItem value="all">
              {t('common.all')}
            </MenuItem>
            {filtersPropertyFaresOptions.boardOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel id="access-code-label">{t('hotels.fares.filters.provider')}</InputLabel>
          <Select
            labelId="access-code-label"
            value={filtersPropertyFares.accessCode || 'all'}
            label="Proveedor"
            size="small"
            onChange={(e) => handleChange('accessCode', e.target.value === 'all' ? '' : e.target.value)}
          >
            <MenuItem value="all">
              {t('common.all')}
            </MenuItem>
            {providerOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" gap={2} sx={{ width: 300 }}>
          <AttachMoney color="primary" />
          <Slider
            disabled={!isSliderEnabled}
            value={filtersPropertyFares.priceRange ?? filtersPropertyFaresOptions.priceRange}
            onChange={(_, newValue) => handleSliderChange(newValue as [number, number])}
            valueLabelDisplay="auto"
            min={filtersPropertyFaresOptions.priceRange[0]}
            max={filtersPropertyFaresOptions.priceRange[1]}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={filtersPropertyFares.reembolsable ?? false}
              onChange={(e) => handleChange('reembolsable', e.target.checked)}
            />
          }
          label={t('hotels.fares.filters.onlyRefundable')}
        />


      </Box>
    </Paper>
  );
};
